/**
 * useProvenanceExportPermission — Checks whether the current user
 * has op:analytics:provenance.export_full via the SDK permission API.
 *
 * Returns 'full_audit' if granted, 'redacted' otherwise.
 * Uses SWR-style caching to avoid repeated PDP round-trips.
 */

const React = (globalThis as any).React;
const { useState, useEffect, useRef } = React;
const { usePlugin } = (globalThis as any).PluginSDK || {};

export type ExportPermissionLevel = 'redacted' | 'full_audit';

export interface UseProvenanceExportPermissionResult {
  permission: ExportPermissionLevel;
  loading: boolean;
  permissionCheckFailed: boolean;
}

const CACHE_TTL_MS = 60_000;
let cachedResult: { permission: ExportPermissionLevel; timestamp: number } | null = null;

export function useProvenanceExportPermission(): UseProvenanceExportPermissionResult {
  const plugin = usePlugin?.() ?? null;
  const sdkApi = plugin?.sdk?.api ?? null;

  const [permission, setPermission] = useState(
    cachedResult && Date.now() - cachedResult.timestamp < CACHE_TTL_MS
      ? cachedResult.permission
      : 'redacted',
  );
  const [loading, setLoading] = useState(!cachedResult);
  const [permissionCheckFailed, setPermissionCheckFailed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_TTL_MS) {
      setPermission(cachedResult.permission);
      setLoading(false);
      return () => { mountedRef.current = false; };
    }

    const resolveFetch = (): typeof globalThis.fetch => {
      if (sdkApi?.fetch) return sdkApi.fetch;
      return globalThis.fetch.bind(globalThis);
    };

    const doFetch = resolveFetch();

    (async () => {
      try {
        const response = await doFetch('/api/v1/authz/check', {
          method: 'POST',
          credentials: 'include' as RequestCredentials,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            application: 'analytics',
            operation: 'op:analytics:provenance.export_full',
            resource_id: 'provenance:export',
          }),
        });

        if (!mountedRef.current) return;

        if (response.ok) {
          const data = await response.json();
          const granted = data?.decision === 'permit' || data?.allowed === true;
          const level: ExportPermissionLevel = granted ? 'full_audit' : 'redacted';
          cachedResult = { permission: level, timestamp: Date.now() };
          setPermission(level);
        }
      } catch {
        if (mountedRef.current) setPermissionCheckFailed(true);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => { mountedRef.current = false; };
  }, [sdkApi]);

  return { permission, loading, permissionCheckFailed };
}

export default useProvenanceExportPermission;
